import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, X, PhoneCall, DollarSign, Mountain, Truck, Zap, MapPin, ArrowRight, Phone, ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
// FAQ Component
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-stone-200 dark:border-white/10 last:border-0" data-id="element-2262">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between py-5 text-left font-bold text-brand-charcoal dark:text-white hover:text-brand-orange transition-colors" data-id="element-2263">
        <span className="text-lg" data-id="element-2264">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-300 shrink-0 ml-4 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2265" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-2266">
        <p className="pb-5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2267">
          {answer}
        </p>
      </motion.div>
    </div>;
}
export function LocalVsNational() {
  const t = useT();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const comparisonData = [{
    attribute: t('lvn.adv.1.attr'),
    local: t('lvn.adv.1.local'),
    national: t('lvn.adv.1.nat')
  }, {
    attribute: t('lvn.adv.2.attr'),
    local: t('lvn.adv.2.local'),
    national: t('lvn.adv.2.nat')
  }, {
    attribute: t('lvn.adv.3.attr'),
    local: t('lvn.adv.3.local'),
    national: t('lvn.adv.3.nat')
  }, {
    attribute: t('lvn.adv.4.attr'),
    local: t('lvn.adv.4.local'),
    national: t('lvn.adv.4.nat')
  }, {
    attribute: t('lvn.adv.5.attr'),
    local: t('lvn.adv.5.local'),
    national: t('lvn.adv.5.nat')
  }, {
    attribute: t('lvn.adv.6.attr'),
    local: t('lvn.adv.6.local'),
    national: t('lvn.adv.6.nat')
  }, {
    attribute: t('lvn.adv.7.attr'),
    local: t('lvn.adv.7.local'),
    national: t('lvn.adv.7.nat')
  }];
  const benefits = [{
    title: t('lvn.why.1.title'),
    desc: t('lvn.why.1.desc'),
    icon: PhoneCall
  }, {
    title: t('lvn.why.2.title'),
    desc: t('lvn.why.2.desc'),
    icon: DollarSign
  }, {
    title: t('lvn.why.3.title'),
    desc: t('lvn.why.3.desc'),
    icon: Mountain
  }, {
    title: t('lvn.why.4.title'),
    desc: t('lvn.why.4.desc'),
    icon: Truck
  }, {
    title: t('lvn.why.5.title'),
    desc: t('lvn.why.5.desc'),
    icon: Zap
  }, {
    title: t('lvn.why.6.title'),
    desc: t('lvn.why.6.desc'),
    icon: MapPin
  }];
  return <div className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen transition-colors duration-300" data-id="element-2268">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-2269">
        <div className="absolute inset-0 z-0" data-id="element-2270">
          <img src="https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png" alt="Dump truck delivery" className="w-full h-full object-cover" data-id="element-2271" />
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80" data-id="element-2272" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2273">
          <div className="max-w-3xl" data-id="element-2274">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1.5 px-4 rounded-xl bg-brand-orange/20 text-brand-orange text-sm font-bold tracking-wider uppercase mb-6" data-id="element-2275">
              {t('lvn.hero.eyebrow')}
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white mb-6 leading-tight" data-id="element-2276">
              {t('lvn.hero.title1')} <br data-id="element-2277" />
              <span className="text-brand-orange" data-id="element-2278">{t('lvn.hero.title2')}</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-300 leading-relaxed mb-8 font-medium font-sub" data-id="element-2279">
              {t('lvn.hero.body')}
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-2280">
              <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2281">
                {t('lvn.hero.btnQuote')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-2282" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-xl font-bold text-base sm:text-lg transition-colors" data-id="element-2283">
                <Phone className="w-5 h-5 mr-2" data-id="element-2284" />
                {t('lvn.hero.btnCall')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2285">
        <div className="text-center mb-16" data-id="element-2286">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2287">
            {t('lvn.advantage.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2288"></div>
          <p className="text-lg text-stone-600 dark:text-stone-300 max-w-2xl mx-auto font-sub" data-id="element-2289">
            {t('lvn.advantage.subtitle')}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-xl overflow-hidden" data-id="element-2290">
          <table className="w-full text-left border-collapse" data-id="element-2291">
            <thead data-id="element-2292">
              <tr className="bg-stone-50 dark:bg-white/5 border-b border-stone-200 dark:border-white/10" data-id="element-2293">
                <th className="p-6 font-bold text-brand-muted w-1/3 text-lg" data-id="element-2294">
                  {t('lvn.advantage.th1')}
                </th>
                <th className="p-6 font-extrabold text-brand-charcoal dark:text-white w-1/3 border-l border-stone-200 dark:border-white/10 bg-brand-orange/5 dark:bg-brand-orange/10" data-id="element-2295">
                  <div className="flex items-center gap-2" data-id="element-2296">
                    <span className="text-brand-orange" data-id="element-2297">253Gravel</span>
                    <span className="text-sm font-medium text-stone-500 dark:text-stone-400" data-id="element-2298">
                      {t('lvn.advantage.th2')}
                    </span>
                  </div>
                </th>
                <th className="p-6 font-extrabold text-stone-500 dark:text-stone-400 w-1/3 border-l border-stone-200 dark:border-white/10" data-id="element-2299">
                  {t('lvn.advantage.th3')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2300">
              {comparisonData.map((row, idx) => <tr key={idx} className="hover:bg-stone-50/50 dark:hover:bg-white/[0.02] transition-colors" data-id="element-2301">
                  <td className="p-6 font-bold text-brand-charcoal dark:text-stone-200" data-id="element-2302">
                    {row.attribute}
                  </td>
                  <td className="p-6 border-l border-stone-200 dark:border-white/10 bg-brand-orange/[0.02] dark:bg-brand-orange/[0.05]" data-id="element-2303">
                    <div className="flex items-start gap-3" data-id="element-2304">
                      <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-2305">
                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" data-id="element-2306" />
                      </div>
                      <span className="font-medium text-brand-charcoal dark:text-white leading-snug" data-id="element-2307">
                        {row.local}
                      </span>
                    </div>
                  </td>
                  <td className="p-6 border-l border-stone-200 dark:border-white/10" data-id="element-2308">
                    <div className="flex items-start gap-3 opacity-70" data-id="element-2309">
                      <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-2310">
                        <X className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-2311" />
                      </div>
                      <span className="font-medium text-stone-600 dark:text-stone-400 leading-snug" data-id="element-2312">
                        {row.national}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-6" data-id="element-2313">
          {comparisonData.map((row, idx) => <div key={idx} className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-md overflow-hidden" data-id="element-2314">
              <div className="bg-stone-50 dark:bg-white/5 p-4 border-b border-stone-200 dark:border-white/10" data-id="element-2315">
                <h3 className="font-bold text-brand-charcoal dark:text-white text-lg" data-id="element-2316">
                  {row.attribute}
                </h3>
              </div>
              <div className="p-4 border-b border-stone-200 dark:border-white/10 bg-brand-orange/[0.02] dark:bg-brand-orange/[0.05]" data-id="element-2317">
                <div className="text-xs font-bold text-brand-orange uppercase tracking-wider mb-2" data-id="element-2318">
                  253Gravel {t('lvn.advantage.th2')}
                </div>
                <div className="flex items-start gap-3" data-id="element-2319">
                  <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-2320">
                    <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" data-id="element-2321" />
                  </div>
                  <span className="font-medium text-brand-charcoal dark:text-white leading-snug" data-id="element-2322">
                    {row.local}
                  </span>
                </div>
              </div>
              <div className="p-4" data-id="element-2323">
                <div className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-2324">
                  {t('lvn.advantage.th3')}
                </div>
                <div className="flex items-start gap-3 opacity-70" data-id="element-2325">
                  <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-2326">
                    <X className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-2327" />
                  </div>
                  <span className="font-medium text-stone-600 dark:text-stone-400 leading-snug" data-id="element-2328">
                    {row.national}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Pullquote Band */}
      <section className="py-24 bg-brand-charcoal dark:bg-black text-center px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-2329">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png')] bg-cover bg-center opacity-10 mix-blend-luminosity" data-id="element-2330" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-2331">
          <Mountain className="w-12 h-12 text-brand-orange mx-auto mb-8 opacity-80" data-id="element-2332" />
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-relaxed mb-8" data-id="element-2333">
            {t('lvn.quote')}
          </h2>
          <div className="w-24 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2334"></div>
        </div>
      </section>

      {/* Benefit Deep Dive */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2335">
        <div className="text-center mb-16" data-id="element-2336">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2337">
            {t('lvn.why.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2338"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-2339">
          {benefits.map((benefit, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} className="bg-white dark:bg-[#2a2a2a] p-8 rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow" data-id="element-2340">
              <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-6" data-id="element-2341">
                <benefit.icon className="w-6 h-6 text-brand-orange" data-id="element-2342" />
              </div>
              <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3 font-heading" data-id="element-2343">
                {benefit.title}
              </h3>
              <p className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2344">
                {benefit.desc}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-stone-100 dark:bg-[#1b1b1b] border-t border-stone-200 dark:border-white/5" data-id="element-2345">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2346">
          <div className="text-center mb-12" data-id="element-2347">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2348">
              {t('lvn.faq.title')}
            </h2>
            <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2349"></div>
          </div>

          <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-6 md:p-8 shadow-sm" data-id="element-2350">
            <FaqItem question={t('lvn.faq.1.q')} answer={t('lvn.faq.1.a')} data-id="element-2351" />
            <FaqItem question={t('lvn.faq.2.q')} answer={t('lvn.faq.2.a')} data-id="element-2352" />
            <FaqItem question={t('lvn.faq.3.q')} answer={t('lvn.faq.3.a')} data-id="element-2353" />
            <FaqItem question={t('lvn.faq.4.q')} answer={t('lvn.faq.4.a')} data-id="element-2354" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2355">
        <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-2356">
          {t('lvn.cta.title')}
        </h2>
        <p className="text-xl text-stone-600 dark:text-stone-300 mb-10 max-w-2xl mx-auto font-sub" data-id="element-2357">
          {t('lvn.cta.body')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2358">
          <Link to="/#materials" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2359">
            {t('lvn.cta.btnQuote')}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-2360" />
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-8 py-4 bg-stone-200 dark:bg-white/10 hover:bg-stone-300 dark:hover:bg-white/20 text-brand-charcoal dark:text-white rounded-xl font-bold text-lg transition-colors" data-id="element-2361">
            <Phone className="w-5 h-5 mr-2" data-id="element-2362" />
            {t('lvn.cta.btnCall')}
          </a>
        </div>
      </section>
    </div>;
}